import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Stack,
  InputAdornment,
  CircularProgress,
  Divider,
  Tooltip,
  IconButton,
  Chip,
} from "@mui/material";
import {
  CasinoOutlined as SlotIcon,
  AccountBalanceWalletOutlined as WalletIcon,
  PercentOutlined as PercentIcon,
  SaveOutlined as SaveIcon,
  RefreshOutlined as RefreshIcon,
  ContentCopy as CopyIcon,
  OpenInNew as ExternalLinkIcon,
  CheckCircleOutline as CheckIcon,
  InfoOutlined as InfoIcon,
  AccountBalanceOutlined as TreasuryIcon,
} from "@mui/icons-material";

import { colors, customShadows, borderRadiusPx } from "../../../../theme";
import {
  CommonButton,
  CommonFormField,
  CommonInput,
  CommonDialog,
  toast,
} from "../../ui";
import slotInstance, { ca as slotContractAddress, getSlotContract } from "../../../Service/slotInstance";
import web3 from "../../../Service/web3";

const DEFAULT_SLOT_TOKEN_SYMBOL = "BET";
const BSC_TESTNET_EXPLORER_BASE = "https://testnet.bscscan.com";

const withTimeout = (promise, ms = 4000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out")), ms)
    ),
  ]);
};

/**
 * AdminSlotCommissionCard
 * Integrates:
 * 1. updateSlot(adminBps) / updateSlotBps(adminBps) - update creator commission percentage cut
 * 2. withdawSlotMargin() / withdrawSlotMargin() - withdraw accumulated commission earnings
 */
const AdminSlotCommissionCard = ({
  adminAccount = "",
  onUpdateSuccess,
  onWithdrawSuccess,
}) => {
  // On-Chain Contract State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentBps, setCurrentBps] = useState(1200); // 12% default
  const [marginBalanceWei, setMarginBalanceWei] = useState("0");
  const [tokenAddress, setTokenAddress] = useState("");
  const [tokenSymbol] = useState(DEFAULT_SLOT_TOKEN_SYMBOL);
  const [contractAdmin, setContractAdmin] = useState("");

  // Update Commission Form State (percentage only, no technical jargon)
  const [inputPercent, setInputPercent] = useState("12");
  const [updatingCut, setUpdatingCut] = useState(false);

  // Withdraw Commission State & Dialog
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawingMargin, setWithdrawingMargin] = useState(false);

  // Copy Feedback
  const [copiedToken, setCopiedToken] = useState(false);

  /**
   * Fetch current slot contract state:
   * - slotAdminBps (current commission cut)
   * - slotAdminMarginBalance (claimable margin)
   * - tokenAddress (settlement token)
   * - admin/owner
   */
  const loadContractData = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const contract = getSlotContract() || slotInstance;
      if (!contract?.methods) {
        console.warn("[AdminSlotCommissionCard] slotInstance methods not available.");
        return;
      }

      // 1. Fetch current creator commission cut (slotAdminBps)
      if (contract.methods.slotAdminBps) {
        try {
          const bps = await withTimeout(contract.methods.slotAdminBps().call(), 3500);
          if (bps !== undefined && bps !== null) {
            const bpsNum = Number(bps);
            setCurrentBps(bpsNum);
            const pct = (bpsNum / 100).toString();
            setInputPercent(pct);
          }
        } catch (err) {
          console.warn("[AdminSlotCommissionCard] Error fetching slotAdminBps:", err);
        }
      }

      // 2. Fetch current accrued commission balance (slotAdminMarginBalance)
      if (contract.methods.slotAdminMarginBalance) {
        try {
          const bal = await withTimeout(
            contract.methods.slotAdminMarginBalance().call(),
            3500
          );
          if (bal !== undefined && bal !== null) {
            setMarginBalanceWei(bal.toString());
          }
        } catch (err) {
          console.warn("[AdminSlotCommissionCard] Error fetching slotAdminMarginBalance:", err);
        }
      }

      // 3. Fetch slot token address (tokenAddress)
      if (contract.methods.tokenAddress) {
        try {
          const tAddr = await withTimeout(contract.methods.tokenAddress().call(), 3500);
          if (tAddr) {
            setTokenAddress(tAddr);
          }
        } catch (err) {
          console.warn("[AdminSlotCommissionCard] Error fetching tokenAddress:", err);
        }
      }

      // 4. Fetch contract admin
      if (contract.methods.admin) {
        try {
          const adm = await withTimeout(contract.methods.admin().call(), 3500);
          if (adm) {
            setContractAdmin(adm);
          }
        } catch (err) {
          console.warn("[AdminSlotCommissionCard] Error fetching slot admin:", err);
        }
      }
    } catch (error) {
      console.error("[AdminSlotCommissionCard] Failed to load slot data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadContractData(false);
  }, [loadContractData]);

  // Handle Percentage Input Change
  const handlePercentChange = (e) => {
    setInputPercent(e.target.value);
  };

  /**
   * Execute updateSlot(adminBps) / updateSlotBps(adminBps)
   * Updates the creator commission percentage on-chain
   */
  const handleUpdateCommissionCut = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const percentNum = parseFloat(inputPercent);
    if (isNaN(percentNum) || percentNum < 0 || percentNum > 100) {
      toast.error("Please enter a valid commission percentage between 0% and 100%.");
      return;
    }

    // Convert clean percentage to smart contract basis points under the hood
    const targetBps = Math.round(percentNum * 100);

    const contract = getSlotContract() || slotInstance;
    if (!contract?.methods) {
      toast.error("Slot smart contract is not initialized.");
      return;
    }

    if (!window.ethereum && !web3) {
      toast.error("Web3 wallet not detected. Please connect MetaMask.");
      return;
    }

    try {
      setUpdatingCut(true);

      let accounts = [];
      if (window.ethereum?.request) {
        accounts = await window.ethereum.request({ method: "eth_accounts" });
      }
      if (!accounts || accounts.length === 0) {
        accounts = await web3.eth.getAccounts();
      }
      const sender = accounts?.[0] || adminAccount;

      if (!sender) {
        toast.error("No active Web3 account found. Please unlock MetaMask.");
        setUpdatingCut(false);
        return;
      }

      // Check admin authorization if available
      if (
        contractAdmin &&
        sender.toLowerCase() !== contractAdmin.toLowerCase()
      ) {
        toast.warning(
          `Connected account (${sender.slice(0, 6)}...${sender.slice(-4)}) does not match slot contract admin (${contractAdmin.slice(0, 6)}...${contractAdmin.slice(-4)}). Transaction may revert.`
        );
      }

      // Call either updateSlotBps or updateSlot
      const methodToCall = contract.methods.updateSlotBps
        ? contract.methods.updateSlotBps(targetBps)
        : contract.methods.updateSlot(targetBps);

      const sendOptions = { from: sender };

      // Gas Estimation
      try {
        const estimatedGas = await methodToCall.estimateGas(sendOptions);
        sendOptions.gas = Math.floor(Number(estimatedGas) * 1.35);
      } catch (gasErr) {
        console.warn("[AdminSlotCommissionCard] Gas estimation failed, fallback to 150000:", gasErr);
        sendOptions.gas = 150000;
      }

      const percentDisplay = percentNum.toFixed(2);
      toast.info(`Updating creator commission to ${percentDisplay}%...`);

      await methodToCall.send(sendOptions);

      toast.success(
        `Creator commission updated to ${percentDisplay}% successfully!`
      );

      // Refresh on-chain state
      await loadContractData(true);

      if (onUpdateSuccess) {
        onUpdateSuccess(targetBps);
      }
    } catch (err) {
      console.error("[AdminSlotCommissionCard] updateSlot error:", err);
      const msg = err?.message || "Transaction cancelled or failed.";
      if (msg.includes("User denied") || msg.includes("rejected")) {
        toast.warning("Transaction was rejected in MetaMask.");
      } else {
        toast.error(`Failed to update commission: ${msg.slice(0, 80)}`);
      }
    } finally {
      setUpdatingCut(false);
    }
  };

  /**
   * Execute withdawSlotMargin() / withdrawSlotMargin()
   * Withdraws accumulated platform slot commission to the admin wallet
   */
  const handleWithdrawMargin = async () => {
    const contract = getSlotContract() || slotInstance;
    if (!contract?.methods) {
      toast.error("Slot smart contract is not initialized.");
      return;
    }

    if (!window.ethereum && !web3) {
      toast.error("Web3 wallet not detected. Please connect MetaMask.");
      return;
    }

    try {
      setWithdrawingMargin(true);

      let accounts = [];
      if (window.ethereum?.request) {
        accounts = await window.ethereum.request({ method: "eth_accounts" });
      }
      if (!accounts || accounts.length === 0) {
        accounts = await web3.eth.getAccounts();
      }
      const sender = accounts?.[0] || adminAccount;

      if (!sender) {
        toast.error("No active Web3 account found. Please unlock MetaMask.");
        setWithdrawingMargin(false);
        return;
      }

      // Check balance first
      const balNum = Number(marginBalanceWei);
      if (balNum <= 0) {
        toast.warning("No commission available to withdraw (balance is 0).");
        setWithdrawingMargin(false);
        setWithdrawModalOpen(false);
        return;
      }

      // Call withdrawSlotMargin or fallback to withdawSlotMargin
      const methodToCall = contract.methods.withdrawSlotMargin
        ? contract.methods.withdrawSlotMargin()
        : contract.methods.withdawSlotMargin();

      const sendOptions = { from: sender };

      // Gas Estimation
      try {
        const estimatedGas = await methodToCall.estimateGas(sendOptions);
        sendOptions.gas = Math.floor(Number(estimatedGas) * 1.35);
      } catch (gasErr) {
        console.warn("[AdminSlotCommissionCard] Gas estimation failed, fallback to 250000:", gasErr);
        sendOptions.gas = 250000;
      }

      toast.info("Submitting slot commission withdrawal to blockchain...");

      await methodToCall.send(sendOptions);

      toast.success("Slot commission withdrawn successfully to your admin wallet!");

      setWithdrawModalOpen(false);

      // Refresh on-chain state
      await loadContractData(true);

      if (onWithdrawSuccess) {
        onWithdrawSuccess();
      }
    } catch (err) {
      console.error("[AdminSlotCommissionCard] withdrawSlotMargin error:", err);
      const msg = err?.message || "Withdrawal cancelled or failed.";
      if (msg.includes("User denied") || msg.includes("rejected")) {
        toast.warning("Withdrawal rejected in MetaMask.");
      } else {
        toast.error(`Withdrawal failed: ${msg.slice(0, 80)}`);
      }
    } finally {
      setWithdrawingMargin(false);
    }
  };

  // Formatted numeric values
  const currentPercentFormatted = (currentBps / 100).toFixed(2);
  const marginBalanceFormatted = marginBalanceWei
    ? (Number(marginBalanceWei) / 10 ** 18).toFixed(5)
    : "0.00000";
  const hasWithdrawableMargin = Number(marginBalanceWei) > 0;

  const truncatedTokenAddress = tokenAddress
    ? `${tokenAddress.substring(0, 8)}...${tokenAddress.substring(tokenAddress.length - 6)}`
    : "0x0000...0000";

  const handleCopyToken = () => {
    if (!tokenAddress) return;
    navigator.clipboard.writeText(tokenAddress);
    setCopiedToken(true);
    toast.success("Token address copied!");
    setTimeout(() => setCopiedToken(false), 2000);
  };

  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.card,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardSubtle,
        backgroundColor: colors.dashboard.cardBg,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: { xs: 2.5, md: 3 },
          pb: 2.5,
          borderBottom: `1px solid ${colors.dashboard.divider}`,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={1.75} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(168, 85, 247, 0.12)",
              color: colors.purple[600],
            }}
          >
            <SlotIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  fontSize: "1.18rem",
                  color: colors.dashboard.title,
                  letterSpacing: "-0.01em",
                }}
              >
                Slot Gaming Commission
              </Typography>
              <Chip
                label="On-Chain"
                size="small"
                sx={{
                  bgcolor: "rgba(16, 185, 129, 0.1)",
                  color: colors.emerald[600],
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  height: 20,
                  borderRadius: "6px",
                }}
              />
            </Stack>
            <Typography
              variant="body2"
              sx={{
                color: colors.slate[500],
                fontSize: "0.84rem",
                mt: 0.25,
              }}
            >
              Manage platform commission on slot machine games and withdraw accumulated earnings.
            </Typography>
          </Box>
        </Stack>

        {/* Header Actions: Refresh & Explorer */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Refresh On-Chain State">
            <IconButton
              size="small"
              onClick={() => loadContractData(true)}
              disabled={refreshing}
              sx={{
                border: `1px solid ${colors.slate[200]}`,
                borderRadius: "8px",
                color: colors.slate[600],
                p: 0.85,
                "&:hover": { bgcolor: colors.slate[100] },
              }}
            >
              <RefreshIcon
                sx={{
                  fontSize: 18,
                  animation: refreshing ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            </IconButton>
          </Tooltip>

          <Tooltip title="View Slot Contract on BSCScan Testnet">
            <IconButton
              size="small"
              component="a"
              href={`${BSC_TESTNET_EXPLORER_BASE}/address/${slotContractAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                border: `1px solid ${colors.slate[200]}`,
                borderRadius: "8px",
                color: colors.purple[600],
                p: 0.85,
                "&:hover": { bgcolor: "rgba(168, 85, 247, 0.08)" },
              }}
            >
              <ExternalLinkIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Overview Stat Cards (Commission Rate + Available Commission + Token) */}
      <Box sx={{ p: { xs: 2.5, md: 3 }, bgcolor: "rgba(248, 250, 252, 0.7)", borderBottom: `1px solid ${colors.dashboard.divider}` }}>
        <Grid container spacing={2}>
          {/* 1. Creator Commission Percentage */}
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                p: 2.25,
                bgcolor: colors.dashboard.cardBg,
                borderRadius: borderRadiusPx.lg,
                border: `1px solid ${colors.dashboard.cardBorder}`,
                boxShadow: customShadows.cardSubtle,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: colors.slate[500],
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      fontSize: "0.75rem",
                    }}
                  >
                    Creator Commission
                  </Typography>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "8px",
                      bgcolor: "rgba(59, 130, 246, 0.1)",
                      color: colors.blue[600],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PercentIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1.5rem", sm: "1.75rem" },
                    color: colors.blue[700],
                    letterSpacing: "-0.02em",
                  }}
                >
                  {currentPercentFormatted}%
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: colors.slate[500],
                  fontSize: "0.75rem",
                  mt: 1,
                  display: "block",
                }}
              >
                Current Rate: <strong>{currentPercentFormatted}%</strong>
              </Typography>
            </Box>
          </Grid>

          {/* 2. Available Commission Earnings (Claimable) */}
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                p: 2.25,
                bgcolor: colors.dashboard.cardBg,
                borderRadius: borderRadiusPx.lg,
                border: `1px solid ${colors.dashboard.cardBorder}`,
                boxShadow: customShadows.cardSubtle,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: colors.slate[500],
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      fontSize: "0.75rem",
                    }}
                  >
                    Available Commission
                  </Typography>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "8px",
                      bgcolor: "rgba(16, 185, 129, 0.1)",
                      color: colors.emerald[600],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TreasuryIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1.5rem", sm: "1.75rem" },
                    color: hasWithdrawableMargin ? colors.emerald[700] : colors.slate[700],
                    letterSpacing: "-0.02em",
                  }}
                >
                  {marginBalanceFormatted}{" "}
                  <Typography
                    component="span"
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: colors.slate[500] }}
                  >
                    {tokenSymbol}
                  </Typography>
                </Typography>
              </Box>
              
            </Box>
          </Grid>

          {/* 3. Slot Settlement Token */}
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                p: 2.25,
                bgcolor: colors.dashboard.cardBg,
                borderRadius: borderRadiusPx.lg,
                border: `1px solid ${colors.dashboard.cardBorder}`,
                boxShadow: customShadows.cardSubtle,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: colors.slate[500],
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      fontSize: "0.75rem",
                    }}
                  >
                    Settlement Token
                  </Typography>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "8px",
                      bgcolor: "rgba(168, 85, 247, 0.1)",
                      color: colors.purple[600],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <WalletIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Stack>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    color: colors.slate[900],
                    letterSpacing: "-0.01em",
                  }}
                >
                  {tokenSymbol} Token
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.75} alignItems="center" mt={1}>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.74rem",
                    color: colors.slate[600],
                  }}
                >
                  {truncatedTokenAddress}
                </Typography>
                {tokenAddress && (
                  <Tooltip title={copiedToken ? "Copied!" : "Copy Token Address"}>
                    <IconButton
                      size="small"
                      onClick={handleCopyToken}
                      sx={{ p: 0.25 }}
                    >
                      <CopyIcon sx={{ fontSize: 13, color: colors.slate[400] }} />
                    </IconButton>
                  </Tooltip>
                )}
                {tokenAddress && (
                  <Tooltip title="View Token on BSCScan">
                    <IconButton
                      size="small"
                      component="a"
                      href={`${BSC_TESTNET_EXPLORER_BASE}/token/${tokenAddress}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ p: 0.25 }}
                    >
                      <ExternalLinkIcon sx={{ fontSize: 13, color: colors.slate[400] }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Main Content Area */}
      <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
        <Grid container spacing={4}>
          {/* LEFT COLUMN: Update Commission */}
          <Grid item xs={12} md={7}>
            <Box
              component="form"
              onSubmit={handleUpdateCommissionCut}
              sx={{
                p: { xs: 2.25, sm: 3 },
                borderRadius: borderRadiusPx.lg,
                border: `1px solid ${colors.dashboard.cardBorder}`,
                bgcolor: colors.dashboard.cardBg,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
                  <PercentIcon sx={{ color: colors.purple[600], fontSize: 20 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.05rem",
                      color: colors.dashboard.title,
                    }}
                  >
                    Update Creator Commission
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  sx={{ color: colors.slate[500], fontSize: "0.82rem", mb: 2.5 }}
                >
                  Set the commission percentage taken by the platform on slot machine games.
                </Typography>

                {/* Single Percentage Input Field */}
                <Box sx={{ maxWidth: 360, mb: 2 }}>
                  <CommonFormField
                    label="Creator Commission (%)"
                    required
                    helperText="Enter a percentage between 0% and 100%"
                  >
                    <CommonInput
                      type="number"
                      value={inputPercent}
                      onChange={handlePercentChange}
                      placeholder="e.g. 12"
                      inputProps={{ min: 0, max: 100, step: "0.1" }}
                      startAdornment={
                        <InputAdornment position="start">
                          <PercentIcon sx={{ fontSize: 17, color: colors.purple[600] }} />
                        </InputAdornment>
                      }
                      endAdornment={<InputAdornment position="end">%</InputAdornment>}
                    />
                  </CommonFormField>
                </Box>

                {/* Live Preview Box */}
                <Box
                  sx={{
                    p: 1.75,
                    borderRadius: "10px",
                    bgcolor: "rgba(241, 245, 249, 0.8)",
                    border: `1px solid ${colors.slate[200]}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    mb: 3,
                  }}
                >
                  <InfoIcon sx={{ fontSize: 18, color: colors.purple[600], flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: colors.slate[700], fontSize: "0.82rem" }}>
                    For every 100 {tokenSymbol} wagered on slots, <strong>{parseFloat(inputPercent || 0).toFixed(2)} {tokenSymbol}</strong> will be collected as creator commission.
                  </Typography>
                </Box>
              </Box>

              {/* Submit Button */}
              <Box>
                <CommonButton
                  type="submit"
                  variant="primary"
                  disabled={updatingCut || loading}
                  startIcon={
                    updatingCut ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  sx={{
                    minWidth: 200,
                    fontWeight: 700,
                  }}
                >
                  {updatingCut ? "Saving Changes..." : "Update Commission"}
                </CommonButton>
              </Box>
            </Box>
          </Grid>

          {/* RIGHT COLUMN: Withdraw Commission */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                p: { xs: 2.25, sm: 3 },
                borderRadius: borderRadiusPx.lg,
                border: `1px solid ${colors.dashboard.cardBorder}`,
                bgcolor: colors.dashboard.cardBg,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
                  <TreasuryIcon sx={{ color: colors.emerald[600], fontSize: 20 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.05rem",
                      color: colors.dashboard.title,
                    }}
                  >
                    Withdraw Commission
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  sx={{ color: colors.slate[500], fontSize: "0.82rem", mb: 2.5 }}
                >
                  Withdraw accumulated slot commission earnings to your connected admin wallet address.
                </Typography>

                {/* Available Balance Box */}
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    bgcolor: hasWithdrawableMargin ? "rgba(16, 185, 129, 0.06)" : colors.slate[50],
                    border: `1px solid ${hasWithdrawableMargin ? "rgba(16, 185, 129, 0.25)" : colors.slate[200]}`,
                    mb: 2.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: colors.slate[500],
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      fontSize: "0.73rem",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    Available To Withdraw:
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: hasWithdrawableMargin ? colors.emerald[700] : colors.slate[800],
                      letterSpacing: "-0.02em",
                      display: "flex",
                      alignItems: "baseline",
                      gap: 1,
                    }}
                  >
                    {marginBalanceFormatted}
                    <Typography
                      component="span"
                      variant="h6"
                      sx={{
                        color: hasWithdrawableMargin ? colors.emerald[600] : colors.slate[500],
                        fontWeight: 700,
                      }}
                    >
                      {tokenSymbol}
                    </Typography>
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.slate[400],
                      fontSize: "0.75rem",
                      display: "block",
                      mt: 0.75,
                    }}
                  >
                    Source: Slot Game Contract
                  </Typography>
                </Box>

                {/* Recipient Information */}
                <Box
                  sx={{
                    p: 1.75,
                    borderRadius: "10px",
                    bgcolor: "rgba(241, 245, 249, 0.7)",
                    border: `1px solid ${colors.slate[200]}`,
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.slate[500],
                      fontWeight: 600,
                      display: "block",
                      mb: 0.25,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Recipient Admin Address:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontFamily: "monospace",
                      fontSize: "0.8rem",
                      color: colors.slate[800],
                      wordBreak: "break-all",
                    }}
                  >
                    {adminAccount || "Connect Admin Wallet"}
                  </Typography>
                </Box>
              </Box>

              {/* Withdraw Button */}
              <Box>
                <CommonButton
                  variant="primary"
                  onClick={() => setWithdrawModalOpen(true)}
                  disabled={withdrawingMargin || loading || !hasWithdrawableMargin}
                  startIcon={
                    withdrawingMargin ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TreasuryIcon />
                    )
                  }
                  sx={{
                    minWidth: 200,
                    fontWeight: 700,
                    bgcolor: hasWithdrawableMargin ? colors.emerald[600] : undefined,
                    "&:hover": {
                      bgcolor: hasWithdrawableMargin ? colors.emerald[700] : undefined,
                    },
                  }}
                >
                  {withdrawingMargin
                    ? "Processing Withdrawal..."
                    : hasWithdrawableMargin
                    ? "Withdraw Commission"
                    : "No Commission to Withdraw"}
                </CommonButton>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>

      {/* Confirmation Dialog */}
      <CommonDialog
        open={withdrawModalOpen}
        onClose={() => !withdrawingMargin && setWithdrawModalOpen(false)}
        title="Withdraw Slot Commission"
        maxWidth="xs"
        actions={
          <>
            <CommonButton
              variant="cancel"
              onClick={() => setWithdrawModalOpen(false)}
              disabled={withdrawingMargin}
            >
              Cancel
            </CommonButton>
            <CommonButton
              variant="contained"
              onClick={handleWithdrawMargin}
              disabled={withdrawingMargin || !hasWithdrawableMargin}
              startIcon={withdrawingMargin ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
              sx={{
                bgcolor: colors.emerald[600],
                "&:hover": { bgcolor: colors.emerald[700] },
              }}
            >
              {withdrawingMargin ? "Confirming..." : "Confirm Withdrawal"}
            </CommonButton>
          </>
        }
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: colors.slate[600] }}>
            You are about to withdraw accumulated slot commission to your admin wallet:
          </Typography>

          <Box
            sx={{
              p: 2,
              borderRadius: borderRadiusPx.md,
              backgroundColor: "rgba(16, 185, 129, 0.06)",
              border: `1px solid rgba(16, 185, 129, 0.25)`,
            }}
          >
            <Typography variant="caption" sx={{ color: colors.slate[500], display: "block" }}>
              Available to Withdraw:
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: colors.emerald[700] }}>
              {marginBalanceFormatted} {tokenSymbol}
            </Typography>

            <Divider sx={{ my: 1.5, borderColor: "rgba(16, 185, 129, 0.2)" }} />

            <Typography variant="caption" sx={{ color: colors.slate[500], display: "block" }}>
              Destination Admin Address:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                wordBreak: "break-all",
                color: colors.slate[800],
                fontFamily: "monospace",
                fontSize: "0.8rem",
              }}
            >
              {adminAccount}
            </Typography>

            <Typography variant="caption" sx={{ color: colors.slate[500], display: "block", mt: 1 }}>
              Contract Address:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                wordBreak: "break-all",
                color: colors.slate[600],
                fontFamily: "monospace",
                fontSize: "0.76rem",
              }}
            >
              {slotContractAddress}
            </Typography>
          </Box>

          <Typography variant="caption" sx={{ color: colors.slate[400], fontStyle: "italic" }}>
            * The funds will be transferred directly to your admin wallet address.
          </Typography>
        </Stack>
      </CommonDialog>
    </Card>
  );
};

AdminSlotCommissionCard.propTypes = {
  adminAccount: PropTypes.string,
  onUpdateSuccess: PropTypes.func,
  onWithdrawSuccess: PropTypes.func,
};

export default AdminSlotCommissionCard;
